import { useCallback, useLayoutEffect, useState, type MutableRefObject } from 'react';
import type { GridApi } from 'ag-grid-community';
import { normalizeMasterRowId } from '../masterDetail/rowModel';

/**
 * Expand/collapse state for MasterDetailGrid, plus an expand-column cell refresh
 * so icons update in the same frame as detail rows appear/disappear.
 */
export const useMasterDetailExpand = ({
  gridApiRef,
  expandColumnId,
}: {
  gridApiRef: MutableRefObject<GridApi | null>;
  expandColumnId: string;
}) => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const isRowExpanded = useCallback(
    (id: string) => expandedIds.has(normalizeMasterRowId(id)),
    [expandedIds]
  );

  const toggleRow = useCallback((id: string) => {
    const normalizedId = normalizeMasterRowId(id);
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(normalizedId) ? next.delete(normalizedId) : next.add(normalizedId);
      return next;
    });
  }, []);

  useLayoutEffect(() => {
    const api = gridApiRef.current;
    if (!api) {
      return;
    }
    api.refreshCells({ columns: [expandColumnId], force: true });
    api.resetRowHeights();
    api.redrawRows?.();
  }, [expandedIds, expandColumnId, gridApiRef]);

  return { isRowExpanded, toggleRow };
};
